import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Clock3, Fingerprint, Globe2, UserCircle } from "lucide-react";

import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/shared/components/ui/sheet";
import { cn } from "@/shared/lib/utils";
import type { AuditLog } from "@/store/apis/adminApi.enhanced";

interface Props {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  log: AuditLog | null;
}

/**
 * Detail del audit log — read-only drawer.
 *
 * Secciones:
 *   1. Who / What / When — identidad del actor + action.
 *   2. Context — IP, user agent, request_id.
 *   3. Diff — `changed_fields` + before/after state (JSON pretty).
 *
 * Decisión: el diff se renderiza como dos columnas side-by-side (before,
 * after). Si hay `changed_fields`, se usan como filtro visual.
 */
export function AuditLogDetailSheet({ open, onOpenChange, log }: Props) {
  if (!log) return null;

  const date = new Date(log.created_at);
  const [resourceType, operation] = splitAction(log.action);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-xl">
        <header className="space-y-3 border-b border-border/60 px-6 py-5">
          <SheetDescription className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
            Audit log · #{log.id}
          </SheetDescription>
          <SheetTitle className="font-display text-[1.35rem] font-medium tracking-display-tight text-foreground">
            <code className="rounded bg-muted/60 px-1.5 py-0.5 font-mono text-[14px]">
              {log.action}
            </code>
          </SheetTitle>
          {resourceType ? (
            <div className="flex flex-wrap items-center gap-2 text-[12.5px] text-muted-foreground">
              <span className="font-mono">{resourceType}</span>
              {operation ? (
                <>
                  <span>·</span>
                  <span className="font-mono">{operation}</span>
                </>
              ) : null}
              {log.resource_id ? (
                <>
                  <span>·</span>
                  <code className="rounded bg-muted/60 px-1.5 py-0.5 font-mono text-[11.5px]">
                    {log.resource_id}
                  </code>
                </>
              ) : null}
            </div>
          ) : null}
        </header>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-6">
          {/* Who / When */}
          <section className="space-y-3">
            <SectionTitle>Actor</SectionTitle>
            <Row
              icon={<UserCircle className="h-3.5 w-3.5" />}
              label="Email"
              value={log.actor_email ?? <Muted>—</Muted>}
              mono
            />
            <Row
              icon={<Fingerprint className="h-3.5 w-3.5" />}
              label="Rol"
              value={log.actor_role ?? <Muted>—</Muted>}
            />
            <Row
              icon={<Clock3 className="h-3.5 w-3.5" />}
              label="Momento"
              value={format(date, "d 'de' MMMM, yyyy · HH:mm:ss", { locale: es })}
            />
          </section>

          {/* Context */}
          <section className="space-y-3">
            <SectionTitle>Contexto</SectionTitle>
            <Row
              icon={<Globe2 className="h-3.5 w-3.5" />}
              label="IP"
              value={log.ip_address ?? <Muted>—</Muted>}
              mono
            />
            {log.request_id ? <Row label="Request" value={log.request_id} mono /> : null}
            {log.user_agent ? (
              <div className="rounded-lg bg-muted/40 p-3">
                <p className="mb-1 font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                  User agent
                </p>
                <p className="break-all font-mono text-[11.5px] text-foreground">
                  {log.user_agent}
                </p>
              </div>
            ) : null}
          </section>

          {/* Diff */}
          {log.before_state || log.after_state ? (
            <section className="space-y-3">
              <SectionTitle>Diff</SectionTitle>
              {log.changed_fields && log.changed_fields.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {log.changed_fields.map((f) => (
                    <span
                      key={f}
                      className="inline-flex rounded-full border border-primary/20 bg-primary/5 px-2 py-0.5 font-mono text-[11px] text-primary"
                    >
                      {f}
                    </span>
                  ))}
                </div>
              ) : null}
              <div className="grid gap-3 md:grid-cols-2">
                <JsonPanel title="Antes" state={log.before_state} />
                <JsonPanel title="Después" state={log.after_state} />
              </div>
            </section>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="border-b border-border/60 pb-2 font-display text-[1rem] font-medium tracking-display-tight text-foreground">
      {children}
    </h3>
  );
}

function Row({
  icon,
  label,
  value,
  mono = false,
}: {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4 text-[13px]">
      <span className="inline-flex items-center gap-1.5 font-mono text-[10.5px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
        {icon}
        {label}
      </span>
      <span
        className={cn("min-w-0 flex-1 text-right text-foreground", mono && "font-mono text-[12px]")}
      >
        {value}
      </span>
    </div>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <span className="text-muted-foreground">{children}</span>;
}

function JsonPanel({ title, state }: { title: string; state: unknown }) {
  const pretty = state ? JSON.stringify(state, null, 2) : null;
  return (
    <div className="overflow-hidden rounded-xl border bg-muted/30">
      <p className="border-b border-border/60 px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
        {title}
      </p>
      {pretty ? (
        <pre className="max-h-72 overflow-auto px-3 py-2 font-mono text-[11.5px] leading-relaxed text-foreground">
          {pretty}
        </pre>
      ) : (
        <p className="px-3 py-6 text-center text-[12px] text-muted-foreground">—</p>
      )}
    </div>
  );
}

/** Separa `advice.create` → ["advice", "create"] con fallback. */
function splitAction(action: string): [string, string | null] {
  const idx = action.lastIndexOf(".");
  if (idx === -1) return [action, null];
  return [action.slice(0, idx), action.slice(idx + 1)];
}
